import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { shell } from '../../shell/shellClasses';
import { formatInt, formatMoney, formatPct } from '../daily-brief/format';
import type { DayPoint, IndiaMapModel, InsightItem, StateAggregate } from './types';
import type { WarehouseMapRow } from './warehouseLayer';

function Collapsible({
  id,
  title,
  defaultOpen,
  children,
  lazy,
}: {
  id: string;
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
  /** Below-fold: body mounts after first expand. */
  lazy?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [mounted, setMounted] = useState(!!defaultOpen || !lazy);
  return (
    <div className={`border-b ${shell.border}`}>
      <button
        type="button"
        id={`${id}-btn`}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        className={`${shell.focusRing} flex w-full min-h-[44px] items-center justify-between gap-2 py-2.5 text-left`}
        onClick={() => {
          setOpen((o) => {
            const next = !o;
            if (next) setMounted(true);
            return next;
          });
        }}
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--shell-fg)]">
          {title}
        </span>
        <span className={`text-sm tabular-nums ${shell.muted}`} aria-hidden>
          {open ? '−' : '+'}
        </span>
      </button>
      {open && mounted && (
        <div id={`${id}-panel`} role="region" aria-labelledby={`${id}-btn`} className="pb-4">
          {children}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className={`text-[10px] font-medium uppercase tracking-wide ${shell.subtle}`}>{label}</dt>
      <dd className="mt-0.5 truncate text-sm font-semibold tabular-nums text-[var(--shell-fg)]">
        {value}
      </dd>
    </div>
  );
}

function MiniTrend({
  points,
  metric,
  currency,
}: {
  points: DayPoint[];
  metric: 'gmv' | 'orders' | 'units';
  currency?: string;
}) {
  const vals = points.map((p) => {
    if (metric === 'gmv') return p.gmv;
    if (metric === 'orders') return p.orders;
    return p.units;
  });
  const numeric = vals.filter((v): v is number => v != null && Number.isFinite(v));
  const max = numeric.length ? Math.max(...numeric, 0) : 0;
  const w = 220;
  const h = 48;
  const pts = points
    .map((_p, i) => {
      const v = vals[i];
      if (v == null || !Number.isFinite(v) || max <= 0) return null;
      const x = points.length <= 1 ? w / 2 : (i / (points.length - 1)) * w;
      const y = h - 4 - (v / max) * (h - 8);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .filter(Boolean);
  const sumOrders = points.reduce((s, p) => s + p.orders, 0);
  const last = points[points.length - 1];

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className={`text-[10px] uppercase ${shell.subtle}`}>{metric.toUpperCase()}</span>
        <span className="text-xs tabular-nums text-[var(--shell-fg)]">
          {metric === 'gmv'
            ? formatMoney(
                points.reduce((s, p) => s + (p.gmv ?? 0), 0) || null,
                currency,
              )
            : metric === 'orders'
              ? formatInt(sumOrders)
              : formatInt(
                  points.every((p) => p.units == null)
                    ? null
                    : points.reduce((s, p) => s + (p.units ?? 0), 0),
                )}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="h-12 w-full text-[var(--shell-accent)]"
        role="img"
        aria-label={`${metric} trend, ${points.length} days`}
      >
        {pts.length > 1 ? (
          <polyline
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={pts.join(' ')}
          />
        ) : (
          <text x={w / 2} y={h / 2} textAnchor="middle" fill="var(--shell-fg-subtle)" fontSize="11">
            {sumOrders === 0 ? 'No data' : '—'}
          </text>
        )}
      </svg>
      {last && (
        <p className={`text-[10px] ${shell.muted}`}>
          through {last.iso}
        </p>
      )}
    </div>
  );
}

function NaBlock({ text }: { text: string }) {
  return <p className={`text-xs ${shell.muted}`}>{text}</p>;
}

function CertDisabled({ label }: { label: string }) {
  return (
    <p className={`rounded-[var(--shell-radius-md)] border border-dashed ${shell.border} px-3 py-2 text-xs ${shell.muted}`}>
      {label}: Available after certification
    </p>
  );
}

/**
 * 360° Geographic Intelligence Drawer — right slide-in.
 * LIVE order + Warehouse Master only. No invent returns/inventory analytics.
 */
export function State360Panel({
  state,
  model,
  warehouses = [],
  onClose,
  onSelectWarehouse,
}: {
  state: StateAggregate;
  model: IndiaMapModel;
  warehouses?: WarehouseMapRow[];
  onClose: () => void;
  onSelectWarehouse?: (w: WarehouseMapRow) => void;
}) {
  const rid = useId();
  const ccy = model.currency;
  const q = `connection=${encodeURIComponent(model.connectionId)}&from=${model.periodFrom}&to=${model.periodTo}&state=${encodeURIComponent(state.code)}`;
  const links = {
    orders: `/amazon/orders?${q}`,
    products: `/amazon/products?${q}`,
    inventory: `/amazon/inventory?${q}`,
    pricing: `/amazon/pricing?${q}`,
    finance: `/amazon/finance?${q}`,
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    // Focus close control for accessibility when opened
    document.getElementById(`${rid}-close`)?.focus();
  }, [rid, state.code]);

  const stateInsights: InsightItem[] = [];
  if (state.nationalRank === 1 && state.contributionPct != null) {
    stateInsights.push({
      id: 's-top',
      tone: 'positive',
      text: `Highest contribution state in geo-known GMV (${state.contributionPct.toFixed(1)}%).`,
    });
  }
  if (state.growthPct != null && state.growthPct > 2) {
    stateInsights.push({
      id: 's-grow',
      tone: 'positive',
      text: `Growing ${state.growthPct.toFixed(1)}% vs compare window.`,
    });
  }
  if (state.growthPct != null && state.growthPct < -2) {
    stateInsights.push({
      id: 's-decline',
      tone: 'watch',
      text: `Declining ${state.growthPct.toFixed(1)}% vs compare window.`,
    });
  }
  if (state.aov != null && state.nationalRank != null && state.nationalRank <= 5) {
    stateInsights.push({
      id: 's-aov',
      tone: 'info',
      text: `High order value — AOV ${formatMoney(state.aov, ccy)} (rank #${state.nationalRank}).`,
    });
  }

  return (
    <>
      {/* Scrim mobile */}
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/45 sm:bg-black/30 xl:hidden"
        aria-label="Close state panel"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${rid}-title`}
        data-panel="state-360"
        className={`fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l ${shell.border} ${shell.surfaceElevated} shadow-2xl transition-transform duration-200 ease-out sm:max-w-md xl:max-w-[min(40vw,28rem)]`}
        style={{ animation: 'state360-in 200ms ease-out' }}
      >
        <style>{`
          @keyframes state360-in {
            from { transform: translateX(100%); opacity: 0.6; }
            to { transform: translateX(0); opacity: 1; }
          }
          @media (prefers-reduced-motion: reduce) {
            [data-panel="state-360"] { animation: none !important; }
          }
        `}</style>

        {/* Sticky header */}
        <header
          className={`sticky top-0 z-10 border-b ${shell.border} ${shell.surfaceElevated} px-4 py-3`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={`text-[10px] font-medium uppercase tracking-widest ${shell.accent}`}>
                360° Geographic Intelligence
              </p>
              <h3
                id={`${rid}-title`}
                className="mt-0.5 truncate text-lg font-semibold tracking-tight text-[var(--shell-fg)]"
              >
                {state.name}
              </h3>
              <p className={`text-xs ${shell.muted}`}>
                {state.code}
                {state.nationalRank != null ? ` · National rank #${state.nationalRank}` : ''}
                {state.contributionPct != null
                  ? ` · ${state.contributionPct.toFixed(1)}% of business`
                  : ''}
              </p>
            </div>
            <button
              id={`${rid}-close`}
              type="button"
              className={`${shell.controlIcon} ${shell.focusRing}`}
              onClick={onClose}
              aria-label="Close 360 state panel"
            >
              ✕
            </button>
          </div>
        </header>

        {/* Scroll body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4">
          <Collapsible id={`${rid}-s1`} title="1 · Executive summary" defaultOpen>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-3">
              <Metric label="State" value={state.name} />
              <Metric
                label="National rank"
                value={state.nationalRank != null ? `#${state.nationalRank}` : 'N/A'}
              />
              <Metric
                label="Contribution %"
                value={
                  state.contributionPct != null
                    ? `${state.contributionPct.toFixed(1)}%`
                    : 'N/A'
                }
              />
              <Metric label="GMV" value={formatMoney(state.gmv, ccy)} />
              <Metric label="Orders" value={formatInt(state.orderCount)} />
              <Metric label="Units" value={formatInt(state.units)} />
              <Metric
                label="Growth %"
                value={
                  state.growthPct != null
                    ? formatPct(state.growthPct)
                    : model.compareLabel
                      ? 'N/A'
                      : 'N/A — enable Compare'
                }
              />
              <Metric label="Average order value" value={formatMoney(state.aov, ccy)} />
              <Metric label="Average selling price" value={formatMoney(state.asp, ccy)} />
            </dl>
          </Collapsible>

          <Collapsible id={`${rid}-s2`} title="2 · Performance trends" defaultOpen>
            <div className="space-y-4">
              <div>
                <p className={`mb-1 text-[10px] font-medium uppercase ${shell.subtle}`}>7D</p>
                <div className="grid gap-3">
                  <MiniTrend points={state.trend7} metric="gmv" currency={ccy} />
                  <MiniTrend points={state.trend7} metric="orders" />
                  <MiniTrend points={state.trend7} metric="units" />
                </div>
              </div>
              <div>
                <p className={`mb-1 text-[10px] font-medium uppercase ${shell.subtle}`}>15D</p>
                <MiniTrend points={state.trend15} metric="gmv" currency={ccy} />
                <MiniTrend points={state.trend15} metric="orders" />
                <MiniTrend points={state.trend15} metric="units" />
              </div>
              <div>
                <p className={`mb-1 text-[10px] font-medium uppercase ${shell.subtle}`}>30D</p>
                <MiniTrend points={state.trend30} metric="gmv" currency={ccy} />
                <MiniTrend points={state.trend30} metric="orders" />
                <MiniTrend points={state.trend30} metric="units" />
              </div>
            </div>
          </Collapsible>

          <Collapsible id={`${rid}-s3`} title="3 · Warehouse intelligence" defaultOpen>
            {warehouses.length === 0 ? (
              <NaBlock text="No warehouses mapped to this state." />
            ) : (
              <div className="space-y-3">
                <dl className="grid grid-cols-2 gap-3">
                  <Metric label="Serving warehouses" value={formatInt(warehouses.length)} />
                  <Metric
                    label="With map pins"
                    value={formatInt(warehouses.filter((w) => w.hasCoords).length)}
                  />
                  <Metric
                    label="Cities"
                    value={
                      [...new Set(warehouses.map((w) => w.city).filter(Boolean))].join(', ') ||
                      'N/A'
                    }
                  />
                </dl>
                <ul className="space-y-2">
                  {warehouses.map((w) => (
                    <li key={w.id}>
                      <button
                        type="button"
                        className={`${shell.control} ${shell.focusRing} flex w-full min-h-[44px] flex-col items-start gap-0.5 text-left`}
                        onClick={() => onSelectWarehouse?.(w)}
                      >
                        <span className="font-mono text-xs text-[var(--shell-fg)]">
                          {w.code} · {w.warehouseType}
                        </span>
                        <span className="text-xs text-[var(--shell-fg)]">{w.name}</span>
                        <span className={`text-[10px] ${shell.muted}`}>
                          {[w.city, w.stateLabel].filter(Boolean).join(', ') || 'Location N/A'} ·{' '}
                          {w.status}
                          {w.hasCoords ? '' : ' · no lat/lon'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <CertDisabled label="Future capacity / warehouse analytics" />
              </div>
            )}
          </Collapsible>

          <Collapsible id={`${rid}-s4`} title="4 · Top products" lazy>
            {state.topProducts.length === 0 ? (
              <NaBlock text="N/A — no ASIN/SKU on LIVE order headers in this scan." />
            ) : (
              <ul className="space-y-2">
                {state.topProducts.map((p) => (
                  <li key={p.key} className="flex flex-col gap-0.5 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-mono text-xs text-[var(--shell-fg)]">
                        {p.key}
                      </span>
                      <Link
                        to={`${links.products}&q=${encodeURIComponent(p.key)}`}
                        className={`${shell.focusRing} shrink-0 text-xs ${shell.accent} underline-offset-2 hover:underline`}
                      >
                        Products
                      </Link>
                    </div>
                    <div className={`flex flex-wrap gap-x-3 text-xs ${shell.muted}`}>
                      <span>Orders {formatInt(p.orders)}</span>
                      <span>Units {formatInt(p.units)}</span>
                      <span>GMV {formatMoney(p.gmv, ccy)}</span>
                      <span>
                        {p.contributionPct != null
                          ? `${p.contributionPct.toFixed(1)}%`
                          : 'N/A'}{' '}
                        contrib
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Collapsible>

          <Collapsible id={`${rid}-s5`} title="5 · Categories" lazy>
            <NaBlock text="Category is not available for these orders." />
          </Collapsible>

          <Collapsible id={`${rid}-s6`} title="6 · Customer behaviour" lazy>
            <dl className="grid grid-cols-2 gap-3">
              <Metric label="COD %" value="N/A" />
              <Metric label="Prepaid %" value="N/A" />
              <Metric label="Repeat orders" value="N/A" />
              <Metric label="Average order value" value={formatMoney(state.aov, ccy)} />
            </dl>
            <p className={`mt-2 text-[10px] ${shell.subtle}`}>
              Only LIVE fields shown. COD/prepaid/repeat need certification.
            </p>
          </Collapsible>

          <Collapsible id={`${rid}-s7`} title="7 · Returns" lazy>
            <CertDisabled label="Customer returns, RTO, return %, return value" />
          </Collapsible>

          {(state.topCities.length > 0 || stateInsights.length > 0) && (
            <Collapsible id={`${rid}-s-extra`} title="Context · Cities & insights" lazy>
              {state.topCities.length > 0 && (
                <ul className="mb-3 space-y-2 text-sm">
                  {state.topCities.map((c) => (
                    <li key={c.city} className="flex items-baseline justify-between gap-2">
                      <span className="truncate font-medium text-[var(--shell-fg)]">{c.city}</span>
                      <span className={`shrink-0 text-xs tabular-nums ${shell.muted}`}>
                        {formatInt(c.orders)} · {formatMoney(c.gmv, ccy)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {stateInsights.length === 0 ? (
                <NaBlock text="No deterministic LIVE rules fired." />
              ) : (
                <ul className="space-y-2">
                  {stateInsights.map((ins) => (
                    <li key={ins.id} className="text-sm leading-snug text-[var(--shell-fg)]">
                      {ins.text}
                    </li>
                  ))}
                </ul>
              )}
            </Collapsible>
          )}
        </div>

        {/* Sticky quick actions */}
        <footer
          className={`sticky bottom-0 z-10 border-t ${shell.border} ${shell.surfaceElevated} px-3 py-2.5`}
        >
          <p className={`mb-2 text-[10px] font-medium uppercase tracking-wide ${shell.subtle}`}>
            8 · Quick actions
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              to={links.orders}
              className={`${shell.control} ${shell.focusRing} min-h-[44px] border-[var(--shell-accent)] text-[var(--shell-accent)]`}
            >
              Orders
            </Link>
            <Link to={links.products} className={`${shell.control} ${shell.focusRing} min-h-[44px]`}>
              Products
            </Link>
            <Link to={links.inventory} className={`${shell.control} ${shell.focusRing} min-h-[44px]`}>
              Inventory
            </Link>
            <Link to={links.pricing} className={`${shell.control} ${shell.focusRing} min-h-[44px]`}>
              Pricing
            </Link>
            <Link to={links.finance} className={`${shell.control} ${shell.focusRing} min-h-[44px]`}>
              Finance
            </Link>
            <button type="button" className={`${shell.control} ${shell.focusRing} min-h-[44px]`} onClick={onClose}>
              Close
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

/** @deprecated Use State360Panel — kept for export compatibility. */
export { State360Panel as StateIntelligenceDrawer };
